import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  NurseMonthlyLimitBulkUpsertRequest,
  NurseMonthlyLimitItem,
  NurseMonthlyLimitListResponse,
} from "@/api/nurses";
import type { NurseResponse } from "@/types/nurse-type";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import {
  invalidateNurseCaches,
  patchNurseNightLimits,
} from "@/services/nurse/nurseCacheInvalidation";

export interface UseNurseMonthlyLimitsParams {
  year: number;
  month: number;
  groupId?: string | null;
  nurseId?: string | null;
  enabled?: boolean;
}

export interface UseNurseMonthlyLimitsResult {
  data: NurseMonthlyLimitListResponse | undefined;
  currentItem: NurseMonthlyLimitItem | null;
  isPending: boolean;
  isFetching: boolean;
  error: Error | null;
}

export function useNurseMonthlyLimits({
  year,
  month,
  groupId,
  nurseId,
  enabled = true,
}: UseNurseMonthlyLimitsParams): UseNurseMonthlyLimitsResult {
  const query = useQuery<NurseMonthlyLimitListResponse, Error>({
    queryKey: QUERY_KEYS.nurses.monthlyLimits(groupId, nurseId),
    queryFn: () =>
      httpClient.get<NurseMonthlyLimitListResponse>("/nurses/monthly-limits", {
        params: {
          ...(groupId && { group_id: groupId }),
          ...(nurseId && { nurse_id: nurseId }),
        },
      }),
    enabled: enabled && !!groupId && !!nurseId,
    staleTime: 5 * 60 * 1000,
  });

  const currentItem =
    nurseId && groupId
      ? (query.data?.items.find(
          (item) =>
            item.nurse_id === nurseId &&
            item.group_id === groupId &&
            item.year === year &&
            item.month === month
        ) ?? null)
      : null;

  return {
    data: query.data,
    currentItem,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
  };
}

export interface UseSaveNurseMonthlyLimitsParams {
  year: number;
  month: number;
  groupId?: string | null;
  /**
   * Group id used by GET /nurses query cache.
   * It can differ from save group id for transferred/inbound rows.
   */
  listGroupId?: string | null;
  nurseId?: string | null;
  onSuccess?: (response: NurseMonthlyLimitListResponse) => void;
  showToast?: boolean;
}

function getMonthlyLimitScopeKey(item: NurseMonthlyLimitItem) {
  return `${item.nurse_id}:${item.group_id}:${item.year}:${item.month}`;
}

function mergeMonthlyLimitResponse(
  previous: NurseMonthlyLimitListResponse | undefined,
  response: NurseMonthlyLimitListResponse,
  request: NurseMonthlyLimitBulkUpsertRequest
): NurseMonthlyLimitListResponse {
  if (!previous) return response;

  const responseItemMap = new Map(
    [...request.limits, ...response.items].map((item) => [
      getMonthlyLimitScopeKey(item),
      item,
    ])
  );
  const previousItemKeys = new Set(
    previous.items.map((item) => getMonthlyLimitScopeKey(item))
  );

  const mergedItems = previous.items.map((item) => {
    return responseItemMap.get(getMonthlyLimitScopeKey(item)) ?? item;
  });

  response.items.forEach((item) => {
    const key = getMonthlyLimitScopeKey(item);
    if (!previousItemKeys.has(key)) {
      mergedItems.push(item);
    }
  });

  return {
    ...previous,
    ...response,
    items: mergedItems,
  };
}

function getChangedMonthlyLimitItems(
  response: NurseMonthlyLimitListResponse,
  request: NurseMonthlyLimitBulkUpsertRequest
): NurseMonthlyLimitItem[] {
  const responseByKey = new Map(
    response.items.map((item) => [getMonthlyLimitScopeKey(item), item])
  );

  return request.limits.map(
    (item) => responseByKey.get(getMonthlyLimitScopeKey(item)) ?? item
  );
}

export function useSaveNurseMonthlyLimits({
  year,
  month,
  groupId,
  listGroupId,
  nurseId,
  onSuccess,
  showToast = true,
}: UseSaveNurseMonthlyLimitsParams) {
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.nurses.monthlyLimits(groupId, nurseId);
  const effectiveListGroupId = listGroupId ?? groupId;

  return useMutation<
    NurseMonthlyLimitListResponse,
    Error,
    NurseMonthlyLimitBulkUpsertRequest
  >({
    mutationFn: (payload) =>
      httpClient.put<NurseMonthlyLimitListResponse>(
        "/nurses/monthly-limits",
        payload
      ),
    onSuccess: async (response, payload) => {
      const changedItems = getChangedMonthlyLimitItems(response, payload);
      queryClient.setQueryData<NurseMonthlyLimitListResponse | undefined>(
        queryKey,
        (previous) => mergeMonthlyLimitResponse(previous, response, payload)
      );
      if (effectiveListGroupId != null) {
        queryClient.setQueriesData<NurseResponse[] | undefined>(
          { queryKey: QUERY_KEYS.nurses.listScope(effectiveListGroupId) },
          (previous) => patchNurseNightLimits(previous, changedItems)
        );
      }
      await invalidateNurseCaches(queryClient, {
        listGroupId: effectiveListGroupId,
        memberGroupId: groupId,
        nurseId: nurseId ?? undefined,
        year,
        month,
      });
      if (showToast) {
        toast.success("근무형태 개수 설정이 저장되었습니다.");
        response.warnings?.forEach((warning) => {
          toast.info(warning.message);
        });
      }
      onSuccess?.(response);
    },
  });
}
